import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, X } from 'lucide-react';
import { Button, Input, cn } from '@scio3/ui';
import { isEnterKey } from '@/lib/keyboard';

type PromoCodeInputProps = {
  onApply: (code: string) => Promise<boolean>;
  appliedCode?: string | null;
  discountLabel?: string | null;
  onRemove?: () => void;
  isLoading?: boolean;
};

export function PromoCodeInput({
  onApply,
  appliedCode,
  discountLabel,
  onRemove,
  isLoading = false,
}: PromoCodeInputProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Editing mode lets users swap an applied coupon for a new code (#48 —
  // scio1 parity: `Update Coupon code` button on the applied-coupon row).
  const [isEditing, setIsEditing] = useState(false);

  const handleApply = useCallback(async () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setError(null);
    const success = await onApply(trimmed);
    if (success) {
      setCode('');
      setIsEditing(false);
    } else {
      setError(t('Invalid coupon code'));
    }
  }, [code, onApply, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isEnterKey(e)) {
        e.preventDefault();
        handleApply();
      }
    },
    [handleApply],
  );

  const handleCancel = useCallback(() => {
    setCode('');
    setError(null);
    setIsEditing(false);
  }, []);

  if (appliedCode && !isEditing) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2"
        data-testid="payment-coupon-applied-row"
      >
        <Tag className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm font-medium flex-1" data-testid="payment-coupon-applied-code">
          {appliedCode}
          {discountLabel && <span className="text-muted-foreground ml-2">({discountLabel})</span>}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="shrink-0 cursor-pointer"
          data-testid="payment-coupon-update-btn"
        >
          {t('Update Coupon code')}
        </Button>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={onRemove}
            aria-label={t('Remove coupon code')}
            className="shrink-0 cursor-pointer"
            data-testid="payment-coupon-remove-btn"
          >
            <X />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="payment-coupon-edit">
      <div className="flex items-center gap-2">
        <Input
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder={t('Coupon code')}
          className={cn('flex-1', error && 'border-destructive')}
          aria-label={t('Coupon code')}
          data-testid="payment-coupon-input"
        />
        <Button
          variant="outline"
          onClick={handleApply}
          disabled={!code.trim() || isLoading}
          className={cn(
            !code.trim() || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
          data-testid="payment-coupon-apply-btn"
        >
          {isLoading ? t('Applying...') : t('Apply')}
        </Button>
        {appliedCode && (
          <Button
            variant="ghost"
            onClick={handleCancel}
            disabled={isLoading}
            className={cn(isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
            data-testid="payment-coupon-cancel-btn"
          >
            {t('Cancel')}
          </Button>
        )}
      </div>
      {error && (
        <p className="text-xs text-destructive" data-testid="payment-coupon-error">
          {error}
        </p>
      )}
    </div>
  );
}
